'use client';

import ButtonsHideAmount from '@components/buttons-hide-amount/ButtonsHideAmount';
import CircleButton from '@components/circle-button/CircleButton';
import Compass from '@components/compass/Compass';
import Switch from '@components/switch/Switch';
import lockIcon from '@images/renders/icon_images/icon-lock.svg';
import { FINCH_FLAT_FORMA_31908, RESERVED_FLATS_31832, SETTLEMENT_INFO_29919 } from '@lib/flags';
import { handleChangeFormName, handleOpenModelForm } from '@lib/store/slices/ComponentsSlice';
import { flatsLocalStorageSelector, removeLocalFlats, setLocalFlats } from '@lib/store/slices/FavoriteFlatSlice';
import { setFlatImg, setFlatInfo, setFlatLoading } from '@lib/store/slices/FlatSlice';
import changeDate from '@lib/tools/_change_date';
import changeEnding from '@lib/tools/_change-ending';
import editRoomsLabel from '@lib/tools/_edit-rooms-label';
import getReservedSimilarParams from '@lib/tools/_get_reserved_similar_params';
import { getFlatPin } from '@lib/tools/_getFlatPin';
import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import NextImage from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import Svg from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import Equalizer from '../equalizer/Equalizer';
import FlatBookButton from '../flat-block-button/FlatBookButton';
import FlatConfigureBtns from '../flat-configure/FlatConfigureBtns';
import FlatFloorsScheme from '../flat-floors-scheme/FlatFloorsScheme';
import { GenplanSignatures } from '../genplan-signatures/GenplanSignatures';
import PriceBlock from '../price-block/PriceBlock';
import WindowViews from '../window-views/WindowViews';
import style from './FlatInfoMobile.module.scss';

const FlatInfoMobile = ({
  flat,
  cdnFloorSvg,
  cdnFloorPng,
  cdnFurnished,
  cdnDimension,
  pathToGenplan,
  pathToGenplanSvg,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isPDF, setIsPDF] = useState(false);
  const [isLink, setIsLink] = useState(false);

  const flatfavorites = useSelector(flatsLocalStorageSelector);
  const genPlan = useSelector(state => state?.flatInfo?.flatGenPlan);
  const flatInfo = useSelector(state => state?.flatInfo?.flatDisplays);
  const activeButton = flatInfo?.flatContent?.active || 'withFurniture';
  const bulk = useSelector(state => state?.flatInfo?.flatInfo?.bulk?.id);
  const allFlats = useSelector(state => state?.filters?.flats);

  const tabsContainerRef = useRef(null);
  const dispatch = useDispatch();

  const rooms = editRoomsLabel(flat);

  const isReserved = flat.status === 'reserve' && RESERVED_FLATS_31832;
  const similarFlatsHref = getReservedSimilarParams(allFlats, flat);

  const furnishedSrc = cdnFurnished;
  const dimensionSrc = cdnDimension;
  const floorSvgSrc = cdnFloorSvg;
  const floorPngSrc = cdnFloorPng;

  const handleOpenForm = () => {
    dispatch(handleOpenModelForm(true));
    dispatch(handleChangeFormName('flatForm'));
  };

  useEffect(() => {
    dispatch(setFlatInfo(flat));
  }, []);

  useEffect(() => {
    if (!flat) return;
    dispatch(
      setFlatLoading({
        id: 1,
        type: 'withFurniture',
        image: furnishedSrc,
      }),
    );
    dispatch(
      setFlatImg([
        { id: 1, image: furnishedSrc, characteristic: 'withFurniture' },
        { id: 2, image: dimensionSrc, characteristic: 'withSize' },
        { id: 3, image: floorSvgSrc, imagePng: floorPngSrc, characteristic: 'onFloor' },
        { id: 4, image: pathToGenplan, characteristic: 'onGenplan' },
      ]),
    );
  }, [dispatch, flat, furnishedSrc, dimensionSrc, floorSvgSrc, floorPngSrc, pathToGenplan]);

  useEffect(() => {
    const isFavorite = flatfavorites?.some(f => f?.id === flat?.id);
    setIsActive(!!isFavorite);
  }, [flatfavorites, flat?.id]);

  const handleLocalStorage = () => {
    if (isActive) {
      dispatch(removeLocalFlats(flat));
    } else {
      dispatch(setLocalFlats(flat));
    }
    setIsActive(prev => !prev);
  };

  const beforeSvgInjection = useCallback(() => {
    const k1 = document.querySelector('#bulk1');
    if (!k1) return;

    const k21 = document.querySelector('#bulk2_1');
    const k22 = document.querySelector('#bulk2_2');
    const k23 = document.querySelector('#bulk2_3');
    if (!k21) return;

    const k1pin = '_1';
    const k21pin = '_2_1';
    const k23pin = '_2_3';
    const k22pin = '_2_2';

    if (flat?.bulk?.id === 9335) {
      k1.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k1pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 9758) {
      k21.classList.add(style.activeHouse);
      k21.style.fill = '#6a8860';
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k21pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 10099) {
      k23.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k23pin}`);
      if (pin) pin.style.opacity = '1';
    } else if (flat?.bulk?.id === 10100) {
      k22.classList.add(style.activeHouse);
      const pin = document.querySelector(`#${getFlatPin(flat.bulk.id, Number(flat.number)) + k22pin}`);
      if (pin) pin.style.opacity = '1';
    }
  }, [flat?.bulk?.id, flat?.number]);

  return (
    <div className={style.flatMobile}>
      <div className={classNames(style.flatMobileDesc, { [style.flatMobile__reserved]: isReserved })}>
        {getTitleText(rooms, flat)}
        <div className={style.flatMobileDesc__box} itemProp='description'>
          <p className={style.flatMobileDesc__desc_text}>{flat?.bulk?.name}</p>
          <p className={style.flatMobileDesc__desc_text}>
            {flat?.floor} этаж из {flat?.section?.floorsCount}
          </p>
          <p className={style.flatMobileDesc__desc_text}>
            <span>№</span> {flat?.number}
          </p>
          {!SETTLEMENT_INFO_29919 && (
            <p className={style.flatMobileDesc__desc_text}>{changeDate(flat?.bulk?.settlement_date)}</p>
          )}
          <p className={style.flatMobileDesc__desc_text}>{changeDate(flat?.bulk?.keyDate, 'Ключи до')}</p>
        </div>
        <div className={style.flatMobileDesc__speacials}>{flat && <ButtonsHideAmount flat={flat} amount={1000} />}</div>
      </div>

      <div className={style.flatMobileDetails}>
        <div className={style.flatMobileDetails__extra}>
          <div className={style.flatMobileDetails__compasbox}>
            {flat && (
              <Compass someStyles={style.compassWrapper} bulk={flat?.bulk_id} genPlanMob={true} genPlan={genPlan} />
            )}
          </div>

          <div
            className={classNames(style.flatMobileDetails__extra__info, {
              [style.flatMobileDetails__extra__infoReserved]: isReserved,
            })}
          >
            <CircleButton
              handleClick={() => setIsPDF(!isPDF)}
              isActive={isPDF}
              genPlan={genPlan}
              flatid={flat?.id}
              someStyles={style.transparentBg}
            >
              <p
                className={classNames(style.flatMobileDetails__extra__pdf, {
                  [style.flatMobileDetails__extra__pdf__white]: genPlan,
                })}
              >
                PDF
              </p>
            </CircleButton>
            <div className={isReserved ? style.flatMobileDetails__extra_shareBtnWrapper : ''}>
              <CircleButton
                handleClick={() => setIsLink(!isLink)}
                isActive={isLink}
                genPlan={genPlan}
                share={true}
                someStyles={style.transparentBg}
              >
                <svg
                  className={classNames(style.flatMobileDetails__extra_svg_one, {
                    [style.whiteSvg]: genPlan,
                  })}
                  viewBox='0 0 16 13'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M10.0774 0.226488C9.89548 0.272297 9.739 0.381668 9.62647 0.541699L9.52051 0.692379L9.51 1.56833L9.49949 2.44432H7.57134C6.47517 2.44432 5.48608 2.46028 5.27912 2.48134C4.32375 2.57848 3.58086 2.84334 2.80566 3.36327C1.67546 4.12124 0.910215 5.2565 0.602211 6.6321C0.521141 6.99421 0.520508 7.01537 0.520508 9.35825V11.7195L0.627523 11.8719C0.912816 12.2783 1.53072 12.3075 1.81524 11.928C1.86249 11.865 2.3795 10.975 2.96415 9.95018C3.5488 8.92537 4.09706 7.98818 4.18256 7.86756C4.45541 7.48246 4.92907 7.16208 5.43039 7.02353C5.63022 6.96826 5.85174 6.9619 7.56934 6.9619H9.48535L9.50293 7.84351C9.52026 8.71212 9.52181 8.72699 9.6084 8.85482C9.75201 9.06677 9.9337 9.17175 10.1847 9.18771C10.3253 9.19668 10.4449 9.18339 10.5237 9.15009C10.5907 9.12176 11.6991 8.22035 12.9869 7.14693C15.5335 5.02433 15.4795 5.07693 15.4795 4.71724C15.4795 4.54058 15.4098 4.33636 15.3112 4.22407C15.1925 4.08907 10.6434 0.316382 10.5477 0.273632C10.4128 0.213375 10.2101 0.193054 10.0774 0.226488Z'
                    fill={isLink ? '#FFFFFF' : '#1d1d1d'}
                  />
                </svg>
              </CircleButton>
            </div>

            <CircleButton
              handleClick={handleLocalStorage}
              isActive={isActive}
              genPlan={genPlan}
              someStyles={style.transparentBg}
              heart={true}
            >
              <svg
                className={`${style.flatMobileDetails__extra_svg_two} ${genPlan ? style.whiteSvg : ''}`}
                viewBox='0 0 16 15'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M3.62193 0.932313C2.11699 1.15627 1.0669 1.88032 0.467674 3.10713C0.0191533 4.02536 -0.126141 5.36992 0.116684 6.35528C0.352189 7.31102 0.916727 8.36296 1.78737 9.46837C2.86195 10.8327 4.52138 12.4145 6.3879 13.8536C6.70427 14.0975 7.83247 14.9179 7.96028 14.997C8.0051 15.0247 8.23372 14.8674 9.06233 14.2389C11.349 12.5044 13.3121 10.6096 14.5125 8.9782C15.6437 7.44102 16.1109 6.15434 15.978 4.94307C15.7221 2.61187 13.8518 0.884854 11.5803 0.882327C10.4135 0.881016 9.34891 1.32788 8.49929 2.17559C8.37026 2.30435 8.20839 2.48261 8.13957 2.57177C8.07076 2.66093 8.00741 2.73388 7.99881 2.73388C7.99021 2.73388 7.92687 2.66093 7.85805 2.57177C7.3161 1.86963 6.4306 1.27256 5.57335 1.03117C5.05229 0.884438 4.22519 0.842543 3.62193 0.932313Z'
                  fill={isActive ? '#FFFFFF' : '#1d1d1d'}
                />
              </svg>
            </CircleButton>
          </div>
        </div>

        <div
          className={classNames(style.flatMobileDetails__flat__container, {
            [style.flatMobileDetails__flat__container_alt]:
              activeButton === 'withFurniture' || activeButton === 'withSize',
          })}
        >
          {!flatInfo?.flatContent?.imagePng ? (
            <>
              {activeButton === 'withFurniture' && (
                <div className={style.imgWrapper}>
                  <NextImage
                    fill
                    src={furnishedSrc}
                    alt='Планировка с мебелью'
                    priority
                    className={
                      genPlan ? style.flatMobileDetails__flat__container__genplan : style.flatMobileDetails__flat
                    }
                    unoptimized={furnishedSrc.endsWith('.svg')}
                    itemProp='image'
                  />
                  <WindowViews flat={flat} />
                </div>
              )}

              {activeButton === 'withSize' && (
                <div className={style.imgWrapper}>
                  <NextImage
                    fill
                    src={dimensionSrc}
                    alt='Планировка с размерами'
                    className={style.flatMobileDetails__flat}
                    unoptimized={dimensionSrc.endsWith('.svg')}
                    itemProp='image'
                  />
                  <WindowViews flat={flat} />
                </div>
              )}

              {genPlan && (
                <div className={style.genplanBox}>
                  <NextImage fill id='gen' className={style.genplanImg} src={pathToGenplan} alt='Генплан' />
                  <Svg id='mask' src={pathToGenplanSvg} className={style.genplanSvg} onLoad={beforeSvgInjection} />
                  <GenplanSignatures />
                </div>
              )}
            </>
          ) : (
            <FlatFloorsScheme
              flatBulk={flat?.bulk_id}
              floorNumber={flat?.floor}
              image={flatInfo?.flatContent?.imagePng}
              flat={flat}
              svg={flatInfo?.flatContent?.image}
              flatNumber={flat?.number}
            />
          )}
        </div>

        <div className={classNames(style.flatMobileDetails__sub__btns)} ref={tabsContainerRef}>
          <FlatConfigureBtns active={activeButton} bulk={bulk} tabsContainerRef={tabsContainerRef} />
        </div>
      </div>

      {isReserved ? (
        <div className={style.reservedBlock}>
          <div className={style.reservedBlock__lockImg}>
            <NextImage width={18} height={18} className={style.reservedBlock__lockImg_icon} src={lockIcon} alt='lock' />
          </div>
          <p className={style.reservedBlock__title}>Квартира забронирована</p>
          <p
            className={style.reservedBlock__description}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                'Посмотрите похожие варианты или подпишитесь<br/> на&nbsp;эту квартиру&nbsp;&mdash; уведомим вас если квартира освободится.',
              ),
            }}
          />
          <div className={style.reservedBlock__buttons}>
            <Switch
              handleSwitch={() => {
                dispatch(handleChangeFormName('reserveFlatForm'));
                dispatch(handleOpenModelForm(true));
              }}
              switchText='Подписаться'
              testId='flatReservedFormOpen'
              black={true}
              someStyles={style.reservedBlock__buttons_btn}
            />
            <Switch
              switchText='Похожие квартиры'
              testId='sameFlatsButton'
              type='link'
              href={similarFlatsHref}
              target='_self'
              someStyles={style.reservedBlock__buttons_btn}
            />
          </div>
        </div>
      ) : (
        <div className={style.flatMobileSub}>
          <Equalizer flat={flat} />
          <PriceBlock flat={flat} />
          {!FINCH_FLAT_FORMA_31908 && <FlatBookButton flat={flat} switchText='Заказать звонок' />}
          {FINCH_FLAT_FORMA_31908 && (
            <div className={style.infoButtons}>
              <Switch handleSwitch={handleOpenForm} switchText='ОСТАВИТЬ ЗАЯВКУ' black testId='mortgageFormOpen' />
              <Switch switchText='Рассчитать ипотеку' type='link' href='#calc' target='_self' />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getTitleText = (rooms, flat) => {
  if (rooms?.rooms === 'Студия') {
    return (
      <p className={style.flatMobileDesc__title} itemProp='name'>
        {rooms?.rooms} {flat?.area}
        <span>
          м<sup>2</sup>
        </span>
      </p>
    );
  }
  if (Number(rooms?.rooms) === 1) {
    return (
      <p className={style.flatMobileDesc__title} itemProp='name'>
        {rooms?.rooms} спальня, {flat?.area}
        <span>
          м<sup>2</sup>
        </span>
      </p>
    );
  }
  return (
    <p className={style.flatMobileDesc__title} itemProp='name'>
      {rooms?.rooms} {changeEnding('bedroom', rooms?.rooms)}, {flat?.area}
      <span>
        м<sup>2</sup>
      </span>
    </p>
  );
};

export default FlatInfoMobile;
